import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { HttpError, HttpService } from 'src/toolkit';
import { TableSort } from 'src/components/table/thead/col';

import { useAppSelector } from '../../hooks';
import { ProductCartListSortCol, TProductCartList, TProductCartListResponse, TAccountProductCartListQuery, adaptProductCartFromListResponse, adaptProductCartListQueryToRequest } from '../../types';
import { BACKEND_ACCOUNT_PRODUCT_CART } from '../../backend-paths';


export const accountProductCartListGetApi = createAsyncThunk(
	BACKEND_ACCOUNT_PRODUCT_CART,
	(payload: TAccountProductCartListQuery | undefined, thunkApi) => {
		const params = adaptProductCartListQueryToRequest(payload);

		return HttpService.get<TProductCartListResponse>(BACKEND_ACCOUNT_PRODUCT_CART, { params }).then(result => {
			return adaptProductCartFromListResponse(result.data);
		}).catch((error: HttpError) => {
			return thunkApi.rejectWithValue({ ...error });
		});
	},
);

type TInitialState = {
	isLoading: boolean;
	list: TProductCartList;
	query: TAccountProductCartListQuery;
	error?: HttpError;
};

const initialState: TInitialState = {
	isLoading: false,
	list: {
		items: [],
		count: 0,
	},
	query: {
		limit: 10,
		offset: 0,
		search: undefined,
		sort: TableSort.ASC,
		sortCol: ProductCartListSortCol.PROVIDER,
	},
	error: undefined,
};

export const accountProductCartListSlice = createSlice({
	name: 'accountProductCartListSlice',
	initialState,
	reducers: {
		clearAccountProductCartList(state) {
			return { ...initialState, list: state.list, query: state.query };
		},

		setAccountProductCartList(state, action: PayloadAction<TProductCartList>) {
			return { ...initialState, list: action.payload, query: state.query };
		},
	},

	extraReducers: {
		[accountProductCartListGetApi.pending.type]: (state) => {
			state.isLoading = true;
			state.error = undefined;
		},

		[accountProductCartListGetApi.fulfilled.type]: (state, action: PayloadAction<TProductCartList>) => {
			state.isLoading = false;
			state.list = action.payload;
			state.error = undefined;
		},

		[accountProductCartListGetApi.rejected.type]: (state, action: PayloadAction<HttpError>) => {
			state.isLoading = false;
			state.error = action.payload;
		},
	},
});

export const useAccountProductCartListSelector = () => useAppSelector(state => state[accountProductCartListSlice.name]);

export const { setAccountProductCartList, clearAccountProductCartList } = accountProductCartListSlice.actions;
